import React, { useContext, useEffect, useState } from 'react';
import { Grid, TextField, Stack, Autocomplete, Typography } from '@mui/material';
import { CreateSurveyContext } from './CreateSurveyContext';
import { useNavigate } from 'react-router-dom';
import { cloneSurvey } from 'services/surveyService';
import { getEngagementLookups } from 'services/engagementService';
import { useAppDispatch } from 'hooks';
import { openNotification } from 'services/notificationService/notificationSlice';
import { MetLabel, PrimaryButton, SecondaryButton } from 'components/shared/common';
import { SurveyLookup } from 'models/survey';
import { EngagementLookup } from 'models/engagement';
import { Disclaimer } from './Disclaimer';
import { Palette } from 'styles/Theme';

export type EngagementParams = {
    engagementId: string;
};

interface CloneOptions {
    availableSurveys: SurveyLookup[] | null;
    loadingSurveys: boolean;
}

const SORT_ORDER = 'asc';

const CloneOptions = ({ availableSurveys, loadingSurveys }: CloneOptions) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const searchParams = new URLSearchParams(location.search);
    const engagementId = searchParams.get('engagementId');
    const [selectedSurvey, setSelectedSurvey] = useState<SurveyLookup | null>(null);
    const [selectedEngagement, setSelectedEngagement] = useState<EngagementLookup | null>(null);
    const [loadingEngagements, setLoadingEngagements] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const {
        surveyForm,
        handleSurveyFormChange,
        availableEngagements,
        setAvailableEngagements,
        isDisclaimerChecked,
        setDisclaimerError,
    } = useContext(CreateSurveyContext);
    const { name } = surveyForm;
    const initialFormError = {
        name: false,
    };
    const [formError, setFormError] = useState(initialFormError);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleSurveyFormChange({
            ...surveyForm,
            [e.target.name]: e.target.value,
        });
    };

    const validate = () => {
        setFormError({
            name: !(surveyForm.name && surveyForm.name.length < 50),
        });
        return Object.values(formError).some((errorExists) => errorExists);
    };

    const getErrorMessage = () => {
        if (name.length > 50) {
            return 'Name must not exceed 50 characters';
        } else if (formError.name) {
            return 'Name must be specified';
        }
        return '';
    };

    const handleFetchEngagements = async (sort_order: 'asc' | 'desc' | undefined) => {
        try {
            const fetchedEngagements = await getEngagementLookups({ sort_order });
            setAvailableEngagements(fetchedEngagements);
            setLoadingEngagements(false);
        } catch (error) {
            dispatch(
                openNotification({ severity: 'error', text: 'Error occurred while fetching available engagements' }),
            );
        }
    };

    useEffect(() => {
        if (!availableEngagements) {
            handleFetchEngagements(SORT_ORDER);
        } else {
            setLoadingEngagements(false);
        }
    }, [availableEngagements]);

    const handleSave = async () => {
        if (!selectedSurvey) {
            dispatch(openNotification({ severity: 'error', text: 'Please select a survey first' }));
            return;
        }
        if (validate()) {
            return;
        }

        if (!isDisclaimerChecked) {
            setDisclaimerError(true);
            return;
        }

        setIsSaving(true);
        try {
            const createdSurvey = await cloneSurvey({
                name: surveyForm.name,
                engagement_id: engagementId ? String(engagementId) : undefined,
                survey_id: selectedSurvey.id,
            });

            dispatch(
                openNotification({
                    severity: 'success',
                    text: 'Survey created, please proceed to building it',
                }),
            );
            navigate(`/surveys/${createdSurvey.id}/build`);
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Error occurred while attempting to save survey',
                }),
            );
            setIsSaving(false);
        }
    };

    return (
        <Grid container direction="row" alignItems="flex-start" justifyContent="flex-start" item xs={12} spacing={2}>
            <Grid item xs={6}>
                <MetLabel sx={{ marginBottom: '2px', display: 'flex' }}>
                    Select Engagement <Typography sx={{ ml: 1, color: Palette.text.disabled }}>(optional)</Typography>
                </MetLabel>

                <Autocomplete
                    id="engagement-selector"
                    options={availableEngagements || []}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label=" "
                            InputLabelProps={{
                                shrink: false,
                            }}
                            fullWidth
                        />
                    )}
                    size="small"
                    getOptionLabel={(engagement: EngagementLookup) => engagement.name}
                    onChange={(_e: React.SyntheticEvent<Element, Event>, engagement: EngagementLookup | null) => {
                        setSelectedEngagement(engagement);
                        if (engagement !== null) setSelectedSurvey(engagement.surveys[0]);
                    }}
                    disabled={loadingEngagements}
                />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <MetLabel sx={{ marginBottom: '2px' }}>Select Survey</MetLabel>
                <Autocomplete
                    id="survey-selector"
                    options={selectedEngagement ? selectedEngagement.surveys : availableSurveys || []}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label=" "
                            InputLabelProps={{
                                shrink: false,
                            }}
                            fullWidth
                        />
                    )}
                    size="small"
                    getOptionLabel={(survey: SurveyLookup) => survey.name}
                    value={selectedSurvey}
                    onChange={(_e: React.SyntheticEvent<Element, Event>, survey: SurveyLookup | null) =>
                        setSelectedSurvey(survey)
                    }
                    disabled={loadingSurveys}
                />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
                <Stack direction="column" spacing={2}>
                    <MetLabel>Enter Survey Name</MetLabel>
                    <TextField
                        id="survey-name"
                        size="small"
                        variant="outlined"
                        label=" "
                        InputLabelProps={{
                            shrink: false,
                        }}
                        fullWidth
                        name="name"
                        value={name}
                        onChange={handleChange}
                        error={formError.name || name.length > 50}
                        helperText={getErrorMessage()}
                    />
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <Disclaimer />
            </Grid>
            <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                    <PrimaryButton onClick={handleSave} loading={isSaving}>
                        {'Save & Continue'}
                    </PrimaryButton>
                    <SecondaryButton onClick={() => navigate(-1)}>Cancel</SecondaryButton>
                </Stack>
            </Grid>
        </Grid>
    );
};

export default CloneOptions;
